// Dashboard widget registry — single source of truth for what can appear in
// the customizable section of the dashboard. Each widget is a self-contained
// component that fetches its own data and renders a .card.
//
// Adding a new widget:
//   1. Create frontend/src/dashboard/widgets/MyWidget.js exporting { meta, default }
//   2. Import + add to WIDGET_REGISTRY below
//   3. Add a default-layout entry in DEFAULT_LAYOUT if it should be visible by default
//
// Layout shape (stored in users.dashboard_layout JSONB):
//   [{ id: 'my_open_tickets', size: 'sm'|'md'|'lg'|'xl', hidden?: boolean }, ...]
// Order in the array = render order. Unknown ids are silently skipped.
import WebAnalyticsWidget, { meta as webAnalyticsMeta } from './widgets/WebAnalyticsWidget';
import MyOpenTicketsWidget, { meta as myOpenTicketsMeta } from './widgets/MyOpenTicketsWidget';
import UpcomingEventsWidget, { meta as upcomingEventsMeta } from './widgets/UpcomingEventsWidget';
import UpcomingBirthdaysWidget, { meta as upcomingBirthdaysMeta } from './widgets/UpcomingBirthdaysWidget';
import LeaderboardWidget, { meta as leaderboardMeta } from './widgets/LeaderboardWidget';
import RecentTicketsWidget, { meta as recentTicketsMeta } from './widgets/RecentTicketsWidget';
import AgentPresenceWidget, { meta as agentPresenceMeta } from './widgets/AgentPresenceWidget';
import TicketsByStatusWidget, { meta as ticketsByStatusMeta } from './widgets/TicketsByStatusWidget';
import TicketsByPriorityWidget, { meta as ticketsByPriorityMeta } from './widgets/TicketsByPriorityWidget';
import TicketTrendWidget, { meta as ticketTrendMeta } from './widgets/TicketTrendWidget';
import MonitorSummaryWidget, { meta as monitorSummaryMeta } from './widgets/MonitorSummaryWidget';
import WorkshopWidget, { meta as workshopMeta } from './widgets/WorkshopWidget';
import BillingQueueWidget, { meta as billingQueueMeta } from './widgets/BillingQueueWidget';
import QuickActionsWidget, { meta as quickActionsMeta } from './widgets/QuickActionsWidget';
import TopCompaniesWidget, { meta as topCompaniesMeta } from './widgets/TopCompaniesWidget';
import TicketChannelsWidget, { meta as ticketChannelsMeta } from './widgets/TicketChannelsWidget';
import OpenByAgentWidget, { meta as openByAgentMeta } from './widgets/OpenByAgentWidget';
import TodayEventsWidget, { meta as todayEventsMeta } from './widgets/TodayEventsWidget';
import BackupStatusWidget, { meta as backupStatusMeta } from './widgets/BackupStatusWidget';
import BackupsOverviewWidget, { meta as backupsOverviewMeta } from './widgets/BackupsOverviewWidget';
import EmailHealthWidget, { meta as emailHealthMeta } from './widgets/EmailHealthWidget';
import WorkerHealthWidget, { meta as workerHealthMeta } from './widgets/WorkerHealthWidget';
import RecentShippedWidget, { meta as recentShippedMeta } from './widgets/RecentShippedWidget';
import MonitorAlertsWidget, { meta as monitorAlertsMeta } from './widgets/MonitorAlertsWidget';
import MyHoursWidget, { meta as myHoursMeta } from './widgets/MyHoursWidget';
import TicketRatingsWidget, { meta as ticketRatingsMeta } from './widgets/TicketRatingsWidget';
import CctvCameraWidget, { meta as cctvCameraMeta } from './widgets/CctvCameraWidget';
import { clientCameraWidgets } from './widgets/ClientCameraWidgets';
import CctvValidationProgressWidget, { meta as cctvProgressMeta } from './widgets/CctvValidationProgressWidget';
import ExchangeRatesWidget, { meta as exchangeRatesMeta } from './widgets/ExchangeRatesWidget';
import DailyQuestWidget, { meta as dailyQuestMeta } from './widgets/DailyQuestWidget';
import TeamActivityWidget, { meta as teamActivityMeta } from './widgets/TeamActivityWidget';
import GitHubActionsWidget, { meta as githubActionsMeta } from './widgets/GitHubActionsWidget';
import VpsHealthWidget, { meta as vpsHealthMeta } from './widgets/VpsHealthWidget';
import OpsStatusWidget, { meta as opsStatusMeta } from './widgets/OpsStatusWidget';
import SonosFleetWidget, { meta as sonosFleetMeta } from './widgets/SonosFleetWidget';
import PlantConnectivityWidget, { meta as plantConnectivityMeta } from './widgets/PlantConnectivityWidget';
import CancillerStudioWidget, { meta as cancillerStudioMeta } from './widgets/CancillerStudioWidget';

export const WIDGET_REGISTRY = [
  { ...opsStatusMeta, Component: OpsStatusWidget },
  { ...plantConnectivityMeta, Component: PlantConnectivityWidget },
  { ...ticketsByStatusMeta, Component: TicketsByStatusWidget },
  { ...ticketsByPriorityMeta, Component: TicketsByPriorityWidget },
  { ...ticketTrendMeta, Component: TicketTrendWidget },
  { ...monitorSummaryMeta, Component: MonitorSummaryWidget },
  { ...workshopMeta, Component: WorkshopWidget },
  { ...billingQueueMeta, Component: BillingQueueWidget },
  { ...recentTicketsMeta, Component: RecentTicketsWidget },
  { ...leaderboardMeta, Component: LeaderboardWidget },
  { ...myOpenTicketsMeta, Component: MyOpenTicketsWidget },
  { ...upcomingEventsMeta, Component: UpcomingEventsWidget },
  { ...upcomingBirthdaysMeta, Component: UpcomingBirthdaysWidget },
  { ...agentPresenceMeta, Component: AgentPresenceWidget },
  { ...webAnalyticsMeta, Component: WebAnalyticsWidget },
  { ...quickActionsMeta, Component: QuickActionsWidget },
  { ...topCompaniesMeta, Component: TopCompaniesWidget },
  { ...ticketChannelsMeta, Component: TicketChannelsWidget },
  { ...openByAgentMeta, Component: OpenByAgentWidget },
  { ...todayEventsMeta, Component: TodayEventsWidget },
  { ...backupStatusMeta, Component: BackupStatusWidget },
  { ...backupsOverviewMeta, Component: BackupsOverviewWidget },
  { ...emailHealthMeta, Component: EmailHealthWidget },
  { ...workerHealthMeta, Component: WorkerHealthWidget },
  { ...recentShippedMeta, Component: RecentShippedWidget },
  { ...monitorAlertsMeta, Component: MonitorAlertsWidget },
  { ...myHoursMeta, Component: MyHoursWidget },
  { ...ticketRatingsMeta, Component: TicketRatingsWidget },
  { ...cctvCameraMeta, Component: CctvCameraWidget },
  { ...cctvProgressMeta, Component: CctvValidationProgressWidget },
  ...clientCameraWidgets.map(w => ({ ...w.meta, Component: w.Component })),
  { ...exchangeRatesMeta, Component: ExchangeRatesWidget },
  { ...dailyQuestMeta, Component: DailyQuestWidget },
  { ...teamActivityMeta, Component: TeamActivityWidget },
  { ...vpsHealthMeta, Component: VpsHealthWidget },
  { ...githubActionsMeta, Component: GitHubActionsWidget },
  { ...sonosFleetMeta, Component: SonosFleetWidget },
  { ...cancillerStudioMeta, Component: CancillerStudioWidget },
];

// Role-aware default layout for users who haven't customized.
// Order roughly matches the legacy dashboard so existing users see something
// familiar on first load (charts + monitor up top, then events/lists below).
export const DEFAULT_LAYOUT = [
  { id: 'daily_quest', size: 'xl' },
  { id: 'ops_status', size: 'md' },
  { id: 'team_activity', size: 'xl' },
  { id: 'tickets_by_status', size: 'md' },
  { id: 'tickets_by_priority', size: 'md' },
  { id: 'ticket_trend', size: 'xl' },
  { id: 'monitor_summary', size: 'sm' },
  { id: 'sonos_fleet', size: 'sm' },
  { id: 'vps_health', size: 'md' },
  { id: 'workshop', size: 'xl' },
  { id: 'billing_queue', size: 'xl' },
  { id: 'recent_tickets', size: 'md' },
  { id: 'leaderboard', size: 'sm' },
  { id: 'my_open_tickets', size: 'sm' },
  { id: 'upcoming_events', size: 'xl' },
  { id: 'upcoming_birthdays', size: 'xl' },
  { id: 'agent_presence', size: 'xl' },
  { id: 'web_analytics', size: 'xl' },
  // quick_actions intentionally NOT in default — auto-appears hidden in the
  // picker so users discover it without surprise.
];

export const SIZE_LABELS = {
  sm: 'dashboard.widgets.size.sm',
  md: 'dashboard.widgets.size.md',
  lg: 'dashboard.widgets.size.lg',
  xl: 'dashboard.widgets.size.xl',
};

// Category metadata for the WidgetPicker drawer. Order here is the order
// categories appear in the picker. Each widget meta has a `category` field
// pointing to one of these keys; unknown/missing categories fall into 'other'.
export const CATEGORY_ORDER = [
  'tickets', 'monitoring', 'calendar', 'team',
  'business', 'system', 'personal', 'news', 'other',
];

// Pre-built layout presets users can switch to with one click. Each preset
// is a curated set of widgets in a sensible order/size for a particular
// role or use case. Applying a preset overwrites the current layout but
// resolveLayout still filters out widgets the user doesn't have access to,
// so an agent picking "Manager view" simply won't see admin-only entries.
export const LAYOUT_PRESETS = [
  {
    id: 'default',
    nameKey: 'dashboard.widgets.preset.default',
    descKey: 'dashboard.widgets.preset.defaultDesc',
    emoji: '🏠',
    get layout() { return DEFAULT_LAYOUT; },
  },
  {
    id: 'personal',
    nameKey: 'dashboard.widgets.preset.personal',
    descKey: 'dashboard.widgets.preset.personalDesc',
    emoji: '👤',
    layout: [
      { id: 'my_open_tickets', size: 'md' },
      { id: 'my_hours', size: 'sm' },
      { id: 'today_events', size: 'sm' },
      { id: 'quick_actions', size: 'xl' },
      { id: 'upcoming_events', size: 'xl' },
      { id: 'upcoming_birthdays', size: 'xl' },
      { id: 'recent_shipped', size: 'md' },
    ],
  },
  {
    id: 'manager',
    nameKey: 'dashboard.widgets.preset.manager',
    descKey: 'dashboard.widgets.preset.managerDesc',
    emoji: '💼',
    layout: [
      { id: 'leaderboard', size: 'md' },
      { id: 'agent_presence', size: 'md' },
      { id: 'open_by_agent', size: 'md' },
      { id: 'ticket_ratings', size: 'md' },
      { id: 'top_companies', size: 'md' },
      { id: 'ticket_channels', size: 'md' },
      { id: 'tickets_by_status', size: 'md' },
      { id: 'tickets_by_priority', size: 'md' },
      { id: 'recent_tickets', size: 'xl' },
    ],
  },
  {
    id: 'system_health',
    nameKey: 'dashboard.widgets.preset.systemHealth',
    descKey: 'dashboard.widgets.preset.systemHealthDesc',
    emoji: '🩺',
    layout: [
      { id: 'ops_status', size: 'md' },
      { id: 'monitor_alerts', size: 'md' },
      { id: 'monitor_summary', size: 'sm' },
      { id: 'backup_status', size: 'sm' },
      { id: 'vps_health', size: 'md' },
      { id: 'email_health', size: 'sm' },
      { id: 'worker_health', size: 'sm' },
      { id: 'workshop', size: 'xl' },
      { id: 'web_analytics', size: 'xl' },
    ],
  },
  {
    id: 'minimal',
    nameKey: 'dashboard.widgets.preset.minimal',
    descKey: 'dashboard.widgets.preset.minimalDesc',
    emoji: '✨',
    layout: [
      { id: 'recent_tickets', size: 'xl' },
      { id: 'monitor_alerts', size: 'md' },
      { id: 'my_open_tickets', size: 'md' },
    ],
  },
];

export const CATEGORY_META = {
  tickets:    { labelKey: 'dashboard.widgets.cat.tickets',    emoji: '🎫' },
  monitoring: { labelKey: 'dashboard.widgets.cat.monitoring', emoji: '🛰️' },
  calendar:   { labelKey: 'dashboard.widgets.cat.calendar',   emoji: '📅' },
  team:       { labelKey: 'dashboard.widgets.cat.team',       emoji: '👥' },
  business:   { labelKey: 'dashboard.widgets.cat.business',   emoji: '💼' },
  system:     { labelKey: 'dashboard.widgets.cat.system',     emoji: '🩺' },
  personal:   { labelKey: 'dashboard.widgets.cat.personal',   emoji: '👤' },
  news:       { labelKey: 'dashboard.widgets.cat.news',       emoji: '📰' },
  other:      { labelKey: 'dashboard.widgets.cat.other',      emoji: '📦' },
};

// iOS-style per-category app-icon colors. Each widget renders its lucide icon
// inside a rounded "squircle" filled with a gradient of its category color,
// giving the gallery the look of an iPhone home screen.
export const CATEGORY_COLORS = {
  tickets:    '#3b82f6', // blue
  monitoring: '#8b5cf6', // violet
  calendar:   '#ef4444', // red
  team:       '#ec4899', // pink
  business:   '#22c55e', // green
  system:     '#14b8a6', // teal
  personal:   '#f59e0b', // amber
  news:       '#6366f1', // indigo
  other:      '#64748b', // slate
};

export function categoryColor(category) {
  return CATEGORY_COLORS[category] || CATEGORY_COLORS.other;
}

// Columns each size spans in the 4-col grid (mobile collapses to 1).
export const SIZE_COLS = { sm: 1, md: 2, lg: 3, xl: 4 };

export function getWidget(id) {
  return WIDGET_REGISTRY.find(w => w.id === id) || null;
}

// Returns true if the user is allowed to see this widget (role + module check).
export function widgetIsAllowed(widget, { user, isAdmin, hasModule }) {
  if (!widget) return false;
  // Hard owner-lock: a widget can be pinned to a single user id (e.g. the CCTV
  // camera widget shows Andy's personal house/office cameras — no other user,
  // not even another admin, should ever see or use it). UX guard; the real
  // protection is the per-camera privacy enforced server-side.
  if (widget.ownerUserId && user?.id !== widget.ownerUserId) return false;
  // allowUserIds: specific user ids that may see the widget regardless of minRole
  // (e.g. a coordination widget granted to one agent). Bypasses the role check.
  const inAllowlist = Array.isArray(widget.allowUserIds) && user && widget.allowUserIds.includes(user.id);
  if (widget.minRole && !inAllowlist) {
    if (widget.minRole === 'admin' && !isAdmin) return false;
    if (widget.minRole === 'manager' && !isAdmin && user?.role !== 'manager') return false;
  }
  if (widget.requiresModule && typeof hasModule === 'function' && !hasModule(widget.requiresModule)) {
    return false;
  }
  return true;
}

// Merge user's saved layout with the registry: keep saved order, drop unknown
// ids, append registry widgets the user has never seen (so new releases of
// widgets appear automatically rather than staying invisible).
export function resolveLayout(savedLayout, authCtx) {
  const base = Array.isArray(savedLayout) && savedLayout.length > 0 ? savedLayout : DEFAULT_LAYOUT;
  const seen = new Set();
  const result = [];
  for (const entry of base) {
    const widget = getWidget(entry.id);
    if (!widget || !widgetIsAllowed(widget, authCtx)) continue;
    if (seen.has(entry.id)) continue;
    seen.add(entry.id);
    result.push({
      id: entry.id,
      size: entry.size || widget.defaultSize || 'md',
      hidden: !!entry.hidden,
      ...(entry.config && typeof entry.config === 'object' ? { config: entry.config } : {}),
    });
  }
  // Widgets que el usuario nunca vio entran OCULTOS (no reordenan nada de lo que
  // ya tenía) pero se colocan ARRIBA de todo, no al final: apenas los prende
  // desde el picker aparecen primeros en la grilla, que es donde uno quiere ver
  // algo recién hecho para probarlo. Antes caían últimos y había que arrastrarlos
  // por toda la página hasta el tope (pedido de Andy).
  const fresh = [];
  for (const widget of WIDGET_REGISTRY) {
    if (seen.has(widget.id)) continue;
    if (!widgetIsAllowed(widget, authCtx)) continue;
    fresh.push({ id: widget.id, size: widget.defaultSize || 'md', hidden: true });
  }
  return [...fresh, ...result];
}
